import { z } from 'zod';

export const poizonGenderSchema = z.enum(['UNISEX', 'MALE', 'FEMALE'], {
    required_error: 'Пол обязателен',
    invalid_type_error: 'Пол должен быть строкой',
});

export type PoizonGenderSchema = z.infer<typeof poizonGenderSchema>;

const parentProductSchema = z.object(
    {
        productId: z.number({
            required_error: 'productId обязателен',
            invalid_type_error: 'productId должен быть числом',
        }),
        parentId: z.undefined(),

        variantId: z.number({
            required_error: 'variantId обязателен',
            invalid_type_error: 'variantId должен быть числом',
        }),

        vendorId: z.number({
            required_error: 'vendorId обязателен',
            invalid_type_error: 'vendorId должен быть числом',
        }),
        brandName: z
            .string({
                invalid_type_error: 'brandName должен быть строкой',
            })
            .optional(),

        categoryId: z.number({
            required_error: 'categoryId обязателен',
            invalid_type_error: 'categoryId должен быть числом',
        }),
        categoryName: z
            .string({
                invalid_type_error: 'categoryName должен быть строкой',
            })
            .optional(),

        title: z.string({
            required_error: 'title обязателен',
            invalid_type_error: 'title должен быть строкой',
        }),
        description: z.string({
            required_error: 'description обязателен',
            invalid_type_error: 'description должен быть строкой',
        }),
        images: z.array(
            z
                .string({ invalid_type_error: 'URL изображения должен быть строкой' })
                .url({ message: 'Некорректный URL изображения' }),
            { invalid_type_error: 'images должен быть массивом URL' },
        ),

        url: z
            .string({
                required_error: 'url обязателен',
                invalid_type_error: 'url должен быть строкой',
            })
            .url({ message: 'Некорректный URL продукта' }),

        gender: poizonGenderSchema.catch(poizonGenderSchema.enum.UNISEX),

        sizes: z
            .union([
                z
                    .array(z.string({ invalid_type_error: 'Размер должен быть строкой' }), {
                        invalid_type_error: 'sizes должен быть массивом',
                    })
                    .optional(),
                z
                    .array(
                        z.object({
                            name: z.string({ invalid_type_error: 'name должен быть строкой' }),
                            delimiter: z.string({ invalid_type_error: 'delimiter должен быть строкой' }),
                            value: z.string({ invalid_type_error: 'value должен быть строкой' }),
                        }),
                        { invalid_type_error: 'Некорректный формат sizes' },
                    )
                    .transform(data => {
                        const sizes = data[0];
                        return sizes?.value.split(sizes.delimiter);
                    }),
            ])
            .pipe(
                z
                    .array(z.string({ invalid_type_error: 'Размер должен быть строкой' }), {
                        invalid_type_error: 'sizes должен быть массивом строк',
                    })
                    .optional(),
            ),

        prices: z
            .record(
                z.string({ invalid_type_error: 'Ключ цены должен быть строкой' }),
                z.number({ invalid_type_error: 'Значение цены должно быть числом' }),
            )
            .optional(),
    },
    {
        invalid_type_error: 'Товар-родитель должен быть объектом',
    },
);

type ParentProductSchema = z.infer<typeof parentProductSchema>;

const childProductSchema = z.object(
    {
        productId: z.number({
            required_error: 'productId обязателен',
            invalid_type_error: 'productId должен быть числом',
        }),
        parentId: z.number({
            required_error: 'parentId обязателен',
            invalid_type_error: 'parentId должен быть числом',
        }),

        url: z
            .string({
                required_error: 'url обязателен',
                invalid_type_error: 'url должен быть строкой',
            })
            .url({ message: 'Некорректный URL продукта' }),

        available: z.boolean({
            required_error: 'available обязателен',
            invalid_type_error: 'available должен быть булевым значением',
        }),
        price: z.number({
            required_error: 'price обязателен',
            invalid_type_error: 'price должен быть числом',
        }),
        purchasePrice: z.number({
            required_error: 'purchasePrice обязателен',
            invalid_type_error: 'purchasePrice должен быть числом',
        }),

        params: z
            .union([
                z.string({ invalid_type_error: 'params должен быть строкой' }).optional(),
                z
                    .array(
                        z.object({
                            key: z.string({ invalid_type_error: 'key должен быть строкой' }),
                            value: z.string({ invalid_type_error: 'value должен быть строкой' }),
                        }),
                        { invalid_type_error: 'params должен быть массивом пар { key, value }' },
                    )
                    .transform(data => {
                        const sizeParam = data.find(param => param.key === '尺码');
                        return sizeParam?.value;
                    }),
            ])
            .pipe(z.string({ invalid_type_error: 'params должен быть строкой' }).optional()),
    },
    {
        invalid_type_error: 'Товар-вариант должен быть объектом',
    },
);

const poizonBaseSchema = z.object(
    {
        categories: z.array(
            z.object(
                {
                    id: z.number({
                        required_error: 'id категории обязателен',
                        invalid_type_error: 'id категории должен быть числом',
                    }),
                    name: z.string({
                        required_error: 'name категории обязателен',
                        invalid_type_error: 'name категории должен быть строкой',
                    }),
                },
                {
                    invalid_type_error: 'Элемент categories должен быть объектом { id, name }',
                },
            ),
            {
                required_error: 'categories обязателен',
                invalid_type_error: 'categories должен быть массивом объектов категорий',
            },
        ),

        brands: z.array(
            z.object(
                {
                    id: z.number({
                        required_error: 'id бренда обязателен',
                        invalid_type_error: 'id бренда должен быть числом',
                    }),
                    name: z.string({
                        required_error: 'name бренда обязателен',
                        invalid_type_error: 'name бренда должен быть строкой',
                    }),
                },
                {
                    invalid_type_error: 'Элемент brands должен быть объектом { id, name }',
                },
            ),
            {
                required_error: 'brands обязателен',
                invalid_type_error: 'brands должен быть массивом объектов брендов',
            },
        ),

        products: z.array(z.union([parentProductSchema, childProductSchema]), {
            required_error: 'products обязателен',
            invalid_type_error: 'products должен быть массивом товаров',
        }),
    },
    {
        required_error: 'Тело запроса обязательно',
        invalid_type_error: 'Ожидался объект верхнего уровня { categories, brands, products }',
    },
);

export const poizonSchema = poizonBaseSchema
    .transform(data => {
        const brandMap = Object.fromEntries(data.brands.map(brand => [brand.id, brand] as const));
        const categoryMap = Object.fromEntries(data.categories.map(category => [category.id, category] as const));

        const parentProductRecord: Record<number, ParentProductSchema> = {};

        for (const product of data.products) {
            if ((product as { parentId?: unknown }).parentId === undefined) {
                parentProductRecord[(product as { productId: number }).productId] = product as ParentProductSchema;
            }
        }

        for (const product of data.products) {
            const parentId = (product as { parentId?: unknown }).parentId;
            if (typeof parentId !== 'number') continue;

            const p = product as {
                parentId: number;
                params?: string;
                price: number;
                available: boolean;
            };

            const parentProduct = parentProductRecord[parentId];
            if (!parentProduct) continue;

            const size = p.params;

            if (size) {
                if (!parentProduct.sizes) parentProduct.sizes = [];
                if (!parentProduct.sizes.includes(size)) {
                    parentProduct.sizes.push(size);
                }
            }

            if (p.available && size && p.price > 0) {
                if (!parentProduct.prices) parentProduct.prices = {};
                parentProduct.prices[size] = p.price;
            }
        }

        for (const parentProduct of Object.values(parentProductRecord)) {
            if ((!parentProduct.sizes || parentProduct.sizes.length === 0) && parentProduct.prices) {
                parentProduct.sizes = Object.keys(parentProduct.prices);
            }
        }

        const products = Object.values(parentProductRecord).map(product => ({
            ...product,
            brandName: brandMap[product.vendorId]?.name ?? product.brandName,
            categoryName: categoryMap[product.categoryId]?.name ?? product.categoryName,
        }));

        return { ...data, products };
    })
    .pipe(
        z.object(
            {
                ...poizonBaseSchema.shape,
                products: z.array(parentProductSchema, {
                    invalid_type_error: 'products должен быть массивом валидных родительских товаров',
                }),
            },
            {
                invalid_type_error: 'Ожидался объект верхнего уровня после нормализации',
                required_error: 'Результат нормализации обязателен',
            },
        ),
    );

export type PoizonPayloadSchema = z.infer<typeof poizonSchema>;

export type PoizonProduct = PoizonPayloadSchema['products'][number];

export type PoizonCategory = PoizonPayloadSchema['categories'][number];

export type PoizonBrand = PoizonPayloadSchema['brands'][number];
